import {track} from '../services/journeyEvents';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {

  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,

  Container, FormControlLabel, Grid, LinearProgress, Stack, Typography,

} from '@mui/material';

import {authService} from '../services/auth';

import {CaptureBalance} from './CaptureBalance';

import { InsufficientCreditsDialog } from './InsufficientCreditsDialog';

import { ArrowBack, Download, Refresh, UploadFile } from '@mui/icons-material';

import { bodyPoseService, BodyPoseApiError, BodyPoseCapabilities, BodyPoseJob } from '../services/bodyPose';

import { newSubmissionKey, validateVideoFile, validateVideoMetadata, VideoMetadata, VIDEO_ACCEPT } from '../utils/bodyPose';



interface BodyPoseMocapProps {

  onBack: () => void;

  onLogin: () => void;

}



function isActive(job: BodyPoseJob): boolean {

  return !['SUCCEEDED', 'REJECTED', 'CANCELLED'].includes(job.state) && (job.state !== 'FAILED' || job.retryable === true);

}

const STAGES: Record<string, string> = {

  DOWNLOADING: 'Preparing your video', PREPARING: 'Adjusting video resolution and preparing clips', SPLITTING: 'Preparing video clips', LOADING_MODELS: 'Preparing capture',

  VALIDATING: 'Checking the video', PROCESSING: 'Capturing body movement', UPLOADING: 'Saving your animation',

};

const STATES: Record<string, string> = {

  SUBMITTING: 'Submitting', QUEUED: 'In queue', STARTING: 'Preparing capture', RUNNING: 'Processing', SUCCEEDED: 'Ready to download',

  CANCELLED: 'Cancelled — no credits charged', REJECTED: 'Video needs changes', FAILED: 'Capture failed', SUBMISSION_UNKNOWN: 'Confirming submission',

};



function mergeJobs(current: BodyPoseJob[], incoming: BodyPoseJob[]): BodyPoseJob[] {

  const merged = new Map(current.map(job => [job.job_id, job]));

  incoming.forEach(job => {

    const previous = merged.get(job.job_id);

    if (!previous || Date.parse(job.updated_at) >= Date.parse(previous.updated_at)) merged.set(job.job_id, job);

  });

  return Array.from(merged.values()).sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at)).slice(0, 50);

}



function formatDate(value: string): string {

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();

}



export const BodyPoseMocap: React.FC<BodyPoseMocapProps> = ({ onBack, onLogin }) => {

  const [capabilities, setCapabilities] = useState<BodyPoseCapabilities | null>(null);

  const [loadingCapabilities, setLoadingCapabilities] = useState(true);

  const [capabilitiesError, setCapabilitiesError] = useState('');

  const [quote,setQuote]=useState<number|null>(null);

  const [quoting,setQuoting]=useState(false);

  const [file, setFile] = useState<File | null>(null);

  const [preview, setPreview] = useState('');

  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);

  const [fileError, setFileError] = useState('');

  const [previewUnavailable, setPreviewUnavailable] = useState(false);

  const [confirmed, setConfirmed] = useState(false);

  const [uploading, setUploading] = useState(false);

  const [jobs, setJobs] = useState<BodyPoseJob[]>([]);

  const [loadingHistory, setLoadingHistory] = useState(true);

  const [error, setError] = useState('');

  const [insufficientCredits, setInsufficientCredits] = useState(false);

  const [statusError, setStatusError] = useState('');

  const [notice, setNotice] = useState('');

  const [sessionExpired, setSessionExpired] = useState(false);

  const [downloading, setDownloading] = useState<string | null>(null);

  const mounted = useRef(false);

  const requests = useRef(new AbortController());

  const input = useRef<HTMLInputElement>(null);

  const submissionKey = useRef('');



  const showError = useCallback((reason: unknown, statusOnly = false) => {

    if (!mounted.current || (reason as Error).name === 'AbortError') return;

    if (reason instanceof BodyPoseApiError && reason.status === 402) {

      setError('');

      if(authService.isAuthenticated()){track('paywall_viewed',{surface:'body'});setInsufficientCredits(true);}else setSessionExpired(true);

      return;

    }

    const message = reason instanceof Error ? reason.message : 'An unexpected error occurred. Please try again.';

    if (reason instanceof BodyPoseApiError && reason.status === 401) setSessionExpired(true);

    if (statusOnly) setStatusError(message);

    else setError(message);

  }, []);



  const loadCapabilities = useCallback(async () => {

    setLoadingCapabilities(true);

    setCapabilitiesError('');

    try {

      const limits = await bodyPoseService.capabilities(requests.current.signal);

      if (mounted.current) setCapabilities(limits);

    } catch (reason) {

      if (!mounted.current || (reason as Error).name === 'AbortError') return;

      setCapabilities(null);

      setCapabilitiesError(reason instanceof Error ? reason.message : 'Could not load your upload limits.');

      if (reason instanceof BodyPoseApiError && reason.status === 401) setSessionExpired(true);

    } finally {

      if (mounted.current) setLoadingCapabilities(false);

    }

  }, []);



  const loadHistory = useCallback(async () => {

    setLoadingHistory(true);

    try {

      const records = await bodyPoseService.list(requests.current.signal);

      if (mounted.current) {

        setJobs(current => mergeJobs(current, records));

        setStatusError('');

      }

    } catch (reason) {

      showError(reason, true);

    } finally {

      if (mounted.current) setLoadingHistory(false);

    }

  }, [showError]);



  useEffect(() => {

    mounted.current = true;

    const controller = new AbortController();

    requests.current = controller;

    if(authService.isAuthenticated())void loadHistory();

    if(authService.isAuthenticated())void loadCapabilities();

    return () => {

      mounted.current = false;

      controller.abort();

    };

  }, [loadHistory, loadCapabilities]);



  useEffect(() => {

    if (!file) { setPreview(''); return; }

    const url = URL.createObjectURL(file);

    setPreview(url);

    return () => URL.revokeObjectURL(url);

  }, [file]);



  useEffect(()=>{setQuote(null);setQuoting(false);if(!file)return;const controller=new AbortController();setQuoting(true);bodyPoseService.estimate(file,controller.signal).then(value=>{if(!controller.signal.aborted)setQuote(value.estimated_credits);}).catch(reason=>{if(!controller.signal.aborted)showError(reason);}).finally(()=>{if(!controller.signal.aborted)setQuoting(false);});return()=>controller.abort();},[file,showError]);

  const pendingIds = jobs.filter(isActive).map(job => job.job_id).join(',');

  useEffect(() => {

    if (!pendingIds || sessionExpired) return;

    const controller = new AbortController();

    let timer: ReturnType<typeof setTimeout>;

    let stopped = false;

    const poll = async () => {

      let failed = false;

      const updated: BodyPoseJob[] = [];

      // Fetch independently so one delayed or unavailable job does not hide other results.

      await Promise.all(pendingIds.split(',').map(async id => {

        try {

          const job = await bodyPoseService.get(id, controller.signal);

          updated.push(job);

        } catch (reason) {

          failed = true;

          if (!stopped) showError(reason, true);

        }

      }));

      if (stopped) return;

      setJobs(current => mergeJobs(current, updated));window.dispatchEvent(new Event('creditsUpdated'));

      if (!failed) setStatusError('');

      timer = setTimeout(poll, 5000);

    };

    void poll();

    return () => { stopped = true; controller.abort(); clearTimeout(timer); };

  }, [pendingIds, sessionExpired, showError]);



  const selectFile = (event: React.ChangeEvent<HTMLInputElement>) => {

    const selected = event.target.files?.[0];

    if (!selected || !capabilities) return;

    const issue = validateVideoFile(selected, capabilities);

    setFile(issue ? null : selected);

    setFileError(issue || '');

    setMetadata(null);

    setPreviewUnavailable(false);

    setConfirmed(false);

    setError('');

    setNotice('');

    submissionKey.current = newSubmissionKey();

  };



  const readMetadata = (event: React.SyntheticEvent<HTMLVideoElement>) => {

    const video = event.currentTarget;

    const info = { duration: video.duration, width: video.videoWidth, height: video.videoHeight };

    setMetadata(info);

    setFileError(validateVideoMetadata(info, capabilities || undefined) || '');

  };



  const submit = async () => {

    if (!file || !capabilities || !confirmed || fileError || uploading || sessionExpired || quote===null) return;

    setUploading(true);

    setError('');

    setNotice('');

    try {

      const job = await bodyPoseService.submit(file, submissionKey.current, requests.current.signal,quote);

      if (!mounted.current) return;

      setJobs(current => mergeJobs(current, [job]));

      setNotice('Your video has been submitted. You can leave this page and return to download the animation.');

      setFile(null);

      setMetadata(null);

      setConfirmed(false);

      if (input.current) input.current.value = '';

    } catch (reason) {

      showError(reason);

      // A lost upload response can still have created a job; refresh before retrying.

      if (mounted.current && (reason as Error).name !== 'AbortError') await loadHistory();

    } finally {

      if (mounted.current) setUploading(false);

    }

  };



  const download = async (job: BodyPoseJob) => {

    setDownloading(job.job_id);

    setError('');

    try {

      const blob = await bodyPoseService.download(job.job_id, requests.current.signal);

      if (!mounted.current) return;

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;

      const basename = job.filename.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 100) || 'body-pose';

      link.download = `${basename}.${job.metadata?.result_format === 'zip' || (job.metadata?.segment_count || 1) > 1 ? 'zip' : 'bvh'}`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);

    } catch (reason) {

      if(reason instanceof BodyPoseApiError && reason.status===401)onLogin();else showError(reason);

    } finally {

      if (mounted.current) setDownloading(null);

    }

  };



  const needsSplitting = !!(capabilities?.premium && file && (

    file.size > capabilities.segment_max_bytes ||

    (metadata && metadata.duration > capabilities.segment_max_seconds + 0.001)

  ));



  if(!authService.isAuthenticated())return <Container maxWidth="md" sx={{py:5}}><Button onClick={onBack}>Back</Button><Typography variant="h3" component="h1" sx={{my:2}}>Body Mocap</Typography><CaptureBalance /><Button variant="contained" onClick={onLogin}>Create an account or sign in</Button></Container>;

  return (

    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>

      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 3 }}>Back to home</Button>

      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>Body Pose Mocap</Typography>

      <CaptureBalance />

      <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 760 }}>

        Turn a video of one person into a body animation. Upload your clip, follow the capture status and download the BVH file when it is ready.

      </Typography>

      <Card component="section" variant="outlined" aria-labelledby="blender-addon-heading" sx={{ mb: 3, borderColor: 'primary.main' }}>

        <CardContent sx={{ p: 3 }}>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">

            <Box>

              <Typography id="blender-addon-heading" variant="h5" component="h2" gutterBottom>Blender addon: BVH / MakeHuman Default</Typography>

              <Typography color="text.secondary" sx={{ mb: 1 }}>The supplied demo shows BVH retargeting to MakeHuman Default in Blender. Check compatibility with your own rig before relying on a result.</Typography>

              <Typography variant="body2" color="text.secondary">The demo does not establish compatibility with other rigs.</Typography>

            </Box>

            <Button component="a" href="/downloads/nina_bvh_makehuman-1.1.1.zip" download="nina_bvh_makehuman-1.1.1.zip" variant="contained" startIcon={<Download />} sx={{ flexShrink: 0 }}>Download addon (.zip)</Button>

          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Install in Blender: Edit &gt; Preferences &gt; Add-ons &gt; Install from Disk. Select the ZIP, enable Nina Body Mocap Retarget, then open the Nina Mocap tab in the 3D View sidebar (N).</Typography>

        </CardContent>

      </Card>

      <Typography color="text.secondary" paragraph>1 whole second of input video = 1 credit; incomplete seconds are rounded down. Body and face videos are separate uploads. Credits are debited only after success. Failed or cancelled captures do not consume credits. Purchased credits do not expire.</Typography>

      <Button data-nina-cta="demo" component="a" href="https://www.youtube.com/watch?v=VfssrYsSl4U" target="_blank" rel="noopener noreferrer">Optional body demo: BVH in Blender / MakeHuman Default</Button>

      {sessionExpired && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={onLogin}>Sign in</Button>}>Sign in again to access your captures.</Alert>}

      <InsufficientCreditsDialog open={insufficientCredits} onClose={() => setInsufficientCredits(false)} />

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {notice && !authService.isAuthenticated() && <Button onClick={onLogin}>Sign in to continue</Button>}

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Grid container spacing={3}>

        <Grid item xs={12} md={5}>

          <Card variant="outlined" sx={{ borderColor: '#404040' }}>

            <CardContent sx={{ p: 3 }}>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>

                <Typography variant="h5" component="h2">New capture</Typography>

                {capabilities?.premium && <Chip label="Premium" color="secondary" size="small" />}

              </Stack>

              <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>

                Keep the camera still and one person fully visible, including their head, hands and feet. Use good lighting and avoid other people in the frame.

              </Typography>

              <Box component="ul" sx={{ pl: 2.5, color: 'text.secondary', mb: 3, '& li': { mb: 0.75 } }}>

                {capabilities && <li>1-{capabilities.max_duration_seconds} seconds (at least 30 normalized frames), 15-120 FPS, up to {capabilities.max_upload_bytes / (1024 * 1024)} MiB.</li>}

                <li>Resolution is adjusted automatically while preserving the video's aspect ratio.</li>

                {capabilities && <li>{capabilities.premium

                  ? `Your active plan includes automatic splitting above ${capabilities.segment_max_seconds} seconds or ${capabilities.segment_max_bytes / (1024 * 1024)} MiB, into up to ${capabilities.max_segments} clips. Prepared clips use additional storage.`

                  : 'Splitting longer or larger videos requires a plan with Body Mocap Premium.'}</li>}

                <li>Output: body animation at 30 FPS. Finger movement is not included.</li>

              </Box>

              {loadingCapabilities && <Typography role="status" variant="body2" color="text.secondary" sx={{ mb: 2 }}>Loading your upload limits...</Typography>}

              {capabilitiesError && <Alert severity="error" sx={{ mb: 2 }} action={

                <Button color="inherit" onClick={() => void loadCapabilities()} disabled={loadingCapabilities || sessionExpired}>Retry</Button>

              }>Could not load your upload limits. {capabilitiesError}</Alert>}

              <Button variant="outlined" component="label" fullWidth startIcon={<UploadFile />} disabled={!capabilities || uploading || sessionExpired}>

                Choose video

                <input ref={input} type="file" accept={VIDEO_ACCEPT} onChange={selectFile} hidden aria-label="Choose video" disabled={!capabilities || uploading || sessionExpired} />

              </Button>

              {file && <Box sx={{ mt: 2 }}>

                <Typography sx={{ overflowWrap: 'anywhere', fontWeight: 600 }}>{file.name}</Typography>

                <Typography variant="body2" color="text.secondary">

                  {(file.size / (1024 * 1024)).toFixed(1)} MiB

                  {metadata && ` Â· ${metadata.duration.toFixed(1)} seconds Â· ${metadata.width} x ${metadata.height}`}

                </Typography>

                {preview && <Box component="video" key={preview} src={preview} controls preload="metadata" playsInline muted

                  aria-label="Selected video preview" onLoadedMetadata={readMetadata}

                  onError={() => setPreviewUnavailable(true)} sx={{ width: '100%', maxHeight: 300, mt: 2, borderRadius: 1, backgroundColor: '#000' }} />}

                {previewUnavailable && <Alert severity="info" sx={{ mt: 1 }}>Your browser cannot preview this format. You can still submit it for video validation.</Alert>}

              </Box>}

              {fileError && <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>}

              {needsSplitting && !fileError && <Alert severity="info" sx={{ mt: 2 }}>This video will be split into smaller clips. Each part produces a BVH animation, available together in one ZIP download.</Alert>}

              {quoting && <Typography>Validating video cost...</Typography>}

              {quote!==null && <Typography>Validated video cost: {quote} credits, debited only after success.</Typography>}

              <FormControlLabel sx={{ mt: 2, alignItems: 'flex-start' }} control={

                <Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={uploading || sessionExpired} />

              } label={<Typography variant="body2" sx={{ pt: 1 }}>I confirm the camera is stationary and only one person is in the video.</Typography>} />

              <Button variant="contained" fullWidth onClick={submit} disabled={!capabilities || !file || !confirmed || !!fileError || uploading || sessionExpired || quote===null || quoting}

                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFile />} sx={{ mt: 2 }}>

                {uploading ? 'Uploading video...' : 'Start body capture'}

              </Button>

              {uploading && <LinearProgress aria-label="Video upload in progress" sx={{ mt: 1 }} />}

              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>

                Your video will be checked before capture. Processing time depends on the clip and the queue. Leave this page after the upload completes.

              </Typography>

            </CardContent>

          </Card>

        </Grid>

        <Grid item xs={12} md={7}>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>

            <Typography variant="h5" component="h2">Your captures</Typography>

            <Button startIcon={<Refresh />} onClick={() => void loadHistory()} disabled={loadingHistory || sessionExpired}>Refresh</Button>

          </Stack>

          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>Your latest 50 captures are saved to your account. Status updates automatically while this page is open.</Typography>

          {statusError && <Alert severity="warning" sx={{ mb: 2 }}>Could not update capture status. {statusError}</Alert>}

          {loadingHistory && !jobs.length && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress aria-label="Loading captures" /></Box>}

          {!loadingHistory && !jobs.length && !statusError && <Card variant="outlined"><CardContent sx={{ py: 5, textAlign: 'center' }}>

            <Typography variant="h6" gutterBottom>No captures yet</Typography>

            <Typography color="text.secondary">Choose a video to create your first body animation.</Typography>

          </CardContent></Card>}

          <Stack spacing={2} aria-live="polite" aria-relevant="additions text">

            {jobs.map(job => {

              const active = isActive(job);

              const frames = job.metadata?.frames ?? job.metadata?.frame_count;

              const segmentCount = job.metadata?.segment_count || job.metadata?.segments?.length || 1;

              const isArchive = job.metadata?.result_format === 'zip' || segmentCount > 1;

              return <Card variant="outlined" key={job.job_id} component="article" aria-label={`Capture ${job.filename}`}>

                <CardContent>

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>

                    <Box sx={{ minWidth: 0 }}>

                      <Typography variant="h6" component="h3" sx={{ overflowWrap: 'anywhere' }}>{job.filename}</Typography>

                      <Typography variant="caption" color="text.secondary">{formatDate(job.created_at)}</Typography>

                    </Box>

                    <Chip label={job.state === 'FAILED' && job.retryable ? 'Waiting for another attempt' : STATES[job.state] || 'Checking status'} size="small"

                      color={job.state === 'SUCCEEDED' ? 'success' : job.state === 'REJECTED' || (job.state === 'FAILED' && !job.retryable) ? 'error' : 'default'} />

                  </Stack>

                  {active && <Box sx={{ mt: 2 }}>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>

                      {job.state === 'SUBMISSION_UNKNOWN' ? 'Your submission is being checked. Please do not send the same video again.'

                        : job.state === 'FAILED' && job.retryable ? 'The capture could not finish. Waiting for another attempt; your video is still saved.'

                        : job.state === 'QUEUED' ? 'Waiting for an available capture slot. You can return later.'

                        : STAGES[job.stage || ''] || 'Preparing your capture...'}

                    </Typography>

                    {segmentCount > 1 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>

                      {job.metadata?.current_segment ? `Part ${job.metadata.current_segment} of ${segmentCount}` : `${segmentCount} video parts`}

                    </Typography>}

                    <LinearProgress aria-label={`Capture progress for ${job.filename}`} />

                  </Box>}

                  {job.error && <Alert severity={job.state === 'SUBMISSION_UNKNOWN' || job.retryable ? 'warning' : 'error'} sx={{ mt: 2 }}>{job.error.message}</Alert>}

                  {job.state === 'SUCCEEDED' && <Box data-nina-result={job.job_id} sx={{ mt: 2 }}>

                    {job.metadata && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>

                      {isArchive ? `${segmentCount} BVH parts in ZIP` : 'BVH'}{frames != null && ` Â· ${frames} frames`}{job.metadata.fps != null && ` Â· ${job.metadata.fps} FPS`}

                      {job.metadata.joint_count != null && ` Â· ${job.metadata.joint_count} body joints`}

                    </Typography>}

                    {job.metadata?.segments && segmentCount > 1 && <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2, color: 'text.secondary' }}>

                      {job.metadata.segments.map((segment, index) => <Typography component="li" variant="body2" key={segment.index}>

                        Part {index + 1}: {segment.start_seconds.toFixed(1)}-{segment.end_seconds.toFixed(1)} seconds

                        {segment.width != null && segment.height != null && ` Â· ${segment.width} x ${segment.height}`}

                      </Typography>)}

                    </Box>}

                    {isActive(job) && <Button onClick={()=>{bodyPoseService.cancel(job.job_id).then(row=>{setJobs(current=>mergeJobs(current,[row]));window.dispatchEvent(new Event('creditsUpdated'));}).catch(reason=>showError(reason));}}>Cancel capture</Button>}

                    <Button variant="contained" startIcon={downloading === job.job_id ? <CircularProgress size={18} color="inherit" /> : <Download />}

                      data-nina-cta="download" onClick={() => void download(job)} disabled={!job.result_available || !!downloading || sessionExpired}>

                      {downloading === job.job_id ? 'Downloading...' : isArchive ? 'Download BVHs (.zip)' : 'Download BVH'}

                    </Button>

                  </Box>}

                </CardContent>

              </Card>;

            })}

          </Stack>

        </Grid>

      </Grid>

    </Container>

  );

};
